/**
 * The `JobsRpc` interface (docs/55 §2.2): what `app`, `console` and `api` call on `jobs` through a service
 * binding. `apps/jobs` implements it as a WorkerEntrypoint. Arguments cross an RPC boundary, so the
 * implementation parses each call with `JOBS_RPC_ARGS` before acting (docs/55 §2.3 "every input parsed").
 */
import { z } from 'zod';
import { Actor } from './actor.ts';
import { JsonValue, text } from './common.ts';
import { ApprovalOutcome, ApprovalSubjectType, ExportKind, OnboardingStep, OnboardingStepState } from './enums.ts';
import { DomainConnectId, ExportId, RequestId, ReviewRequestId, StaffId, SupportTicketId, TenantId, UserId, WorkflowId } from './ids.ts';
import { SupportSignal, SupportStart } from './support.ts';
import { PublishReason } from './queues.ts';
import { ReportKey } from './report-period.ts';
import { ClientScans, ConsoleReport, ConsoleReportList, GridSetBody, MoneyServicesSetBody, MoneyServicesView, ProspectMap, ReportActionBody, ScanBudgetView, ScanGridView, ScanRoster, ScanStartBody, ScanStartResult } from './console/schemas.ts';
import { GridPoints } from './api/reports.ts';
import { GridRunId } from './ids.ts';
import { ProspectReplyActionBody, ProspectReplyId, ProspectReplyIn, ProspectReplyIntakeResult, ProspectReplyQueue, ProspectReplyQueueItem } from './prospect-replies.ts';

export { OnboardingStep, ExportKind, PublishReason };

export const ApprovalSubject = z.object({ type: ApprovalSubjectType, id: z.string().regex(/^[a-z]{2,5}_[a-z0-9]{6,40}$/) });
export type ApprovalSubject = z.infer<typeof ApprovalSubject>;

/** Why a tenant is offboarded: the plan ended after a cancel, staff refunded a site that never went live (case by case, c044), or staff closed it (docs/53 §7.1). */
export const OffboardReason = z.enum(['canceled', 'refunded', 'closed']);
export type OffboardReason = z.infer<typeof OffboardReason>;

/**
 * What changed in a tenant's photo library (k11), told to `jobs` by the app and the console: `uploaded` and
 * `removed` by the client, `reviewed` (approved or turned down), `placed` (a slot or a focal point) by staff. Jobs
 * re-reads the photos rule for the onboarding step and, when the site's pages could change, queues a publish.
 */
export const MediaChange = z.enum(['uploaded', 'reviewed', 'placed', 'removed']);
export type MediaChange = z.infer<typeof MediaChange>;

/** The signed part of an export link: `<expiry unix seconds>.<base64url HMAC-SHA256>`, minted and checked by `jobs`. */
export const ExportLinkToken = z.string().regex(/^\d{10}\.[A-Za-z0-9_-]{43}$/);

export const ApprovalDecision = z.object({
  decision: ApprovalOutcome,
  actor: Actor,
  typedName: text(80, 2).optional(),
  stepUp: z.boolean(),
  note: text(1000).optional(),
});
export type ApprovalDecision = z.infer<typeof ApprovalDecision>;

/** A staff answer to a domain-connect run that is blocked or waiting (docs/32): look again, accept a named risk, or stop. */
export const DomainSignal = z.discriminatedUnion('action', [
  z.object({ action: z.literal('rerun') }).strict(),
  z.object({ action: z.literal('ack'), ack: z.enum(['public_only', 'sources_disagree']) }).strict(),
  z.object({ action: z.literal('abort'), reason: text(300, 3) }).strict(),
]);
export type DomainSignal = z.infer<typeof DomainSignal>;

export const JOBS_RPC_ARGS = {
  startOnboarding: z.tuple([TenantId]),
  signalOnboarding: z.tuple([TenantId, OnboardingStep, JsonValue]),
  startChange: z.tuple([RequestId]),
  decide: z.tuple([ApprovalSubject, ApprovalDecision]),
  publish: z.tuple([TenantId, PublishReason]),
  rollback: z.tuple([TenantId, z.number().int().min(1), Actor]),
  /** The requester: a client user, or staff (c013: an export with the site files is staff only). */
  startExport: z.tuple([TenantId, z.union([UserId, StaffId]), ExportKind]),
  scheduleReviewRequest: z.tuple([ReviewRequestId]),
  cancelReviewRequest: z.tuple([ReviewRequestId]),
  tenantSocket: z.tuple([TenantId, z.instanceof(Request)]),
  startOffboarding: z.tuple([TenantId, OffboardReason]),
  // Client domains (docs/32): start the nameserver move for a domain, and answer a run that is waiting on a person.
  startDomainConnect: z.tuple([TenantId, DomainConnectId]),
  signalDomainConnect: z.tuple([TenantId, DomainConnectId, DomainSignal]),
  exportDownload: z.tuple([TenantId, ExportId, ExportLinkToken]),
  // Scans, maps and reports (docs/55 §3.1 step 9). The console calls these; the actor is the staff member.
  scanOverview: z.tuple([TenantId]),
  setScanGrid: z.tuple([TenantId, GridSetBody, Actor]),
  setMoneyServices: z.tuple([TenantId, MoneyServicesSetBody, Actor]),
  startScan: z.tuple([TenantId, ScanStartBody, Actor]),
  scanPoints: z.tuple([TenantId, GridRunId]),
  scanRoster: z.tuple([TenantId, GridRunId]),
  prospectMap: z.tuple([TenantId, GridRunId]),
  scanBudget: z.tuple([]),
  reportList: z.tuple([TenantId]),
  reportGet: z.tuple([TenantId, ReportKey]),
  reportAction: z.tuple([TenantId, ReportKey, ReportActionBody, Actor]),
  mediaChanged: z.tuple([TenantId, MediaChange]),
  // The payment ladder's two entry points, called by the app's Stripe webhook (docs/60 s138).
  paymentFailed: z.tuple([TenantId]),
  paymentRecovered: z.tuple([TenantId]),
  // The support desk (SUP-D1): a request becomes a ticket the support Workflow prepares; the console signals it.
  startSupport: z.tuple([RequestId, SupportStart]),
  supportSignal: z.tuple([SupportTicketId, SupportSignal]),
  // Replies to email one (docs/10 §8, docs/90 wave 3): the app's Instantly webhook and the console's paste, the queue, a person's action.
  replyIntake: z.tuple([ProspectReplyIn, Actor]),
  replyQueue: z.tuple([z.enum(['waiting', 'all'])]),
  replyAction: z.tuple([ProspectReplyId, ProspectReplyActionBody, Actor]),
} as const;

/**
 * What a rollback did beyond the pointer (Stage 1): the content the target version was rendered from, written back
 * to D1 so the next publish doesn't bring the bad content back. `contentRestored` is false when the version predates
 * content snapshots or the restore failed; `skipped` names what couldn't be put back (a service added or removed since).
 */
export const RollbackResult = z.object({
  contentRestored: z.boolean(),
  restored: z.number().int().min(0),
  skipped: z.array(z.string().max(120)).max(50),
  note: z.string().max(300),
});
export type RollbackResult = z.infer<typeof RollbackResult>;

export const JOBS_RPC_RESULTS = {
  startOnboarding: z.object({ workflowId: WorkflowId }),
  signalOnboarding: z.void(),
  startChange: z.object({ workflowId: WorkflowId }),
  decide: z.void(),
  publish: z.object({ version: z.number().int().min(1) }),
  rollback: RollbackResult,
  startExport: z.object({ exportId: ExportId }),
  scheduleReviewRequest: z.void(),
  cancelReviewRequest: z.void(),
  tenantSocket: z.instanceof(Response),
  startOffboarding: z.object({ workflowId: WorkflowId }),
  startDomainConnect: z.object({ workflowId: WorkflowId }),
  signalDomainConnect: z.void(),
  exportDownload: z.instanceof(Response),
  scanOverview: ClientScans,
  setScanGrid: ScanGridView,
  setMoneyServices: MoneyServicesView,
  startScan: ScanStartResult,
  scanPoints: GridPoints,
  scanRoster: ScanRoster,
  prospectMap: ProspectMap,
  scanBudget: ScanBudgetView,
  reportList: ConsoleReportList,
  reportGet: ConsoleReport,
  reportAction: ConsoleReport,
  mediaChanged: z.object({ photosStep: OnboardingStepState.nullable(), publishQueued: z.boolean() }),
  paymentFailed: z.object({ day: z.number().int().min(0), did: z.array(z.string().max(40)).max(10) }).nullable(),
  paymentRecovered: z.object({ resumed: z.boolean() }),
  /** `workflowId` is null where the support Workflow isn't bound: the request then went to the change Workflow as before. */
  startSupport: z.object({ ticketId: SupportTicketId.nullable(), workflowId: WorkflowId.nullable() }),
  supportSignal: z.void(),
  replyIntake: ProspectReplyIntakeResult,
  replyQueue: ProspectReplyQueue,
  replyAction: ProspectReplyQueueItem,
} as const;

/** docs/55 §2.2, exactly. Ids are the branded contract strings. */
export interface JobsRpc {
  startOnboarding(tenantId: string): Promise<{ workflowId: string }>;
  signalOnboarding(tenantId: string, step: OnboardingStep, evidence: unknown): Promise<void>;
  startChange(requestId: string): Promise<{ workflowId: string }>;
  /** Sends the Workflow's waitForEvent. */
  decide(subject: ApprovalSubject, decision: ApprovalDecision): Promise<void>;
  publish(tenantId: string, reason: PublishReason): Promise<{ version: number }>;
  /** Moves the pointer back, then restores the content that version was rendered from (Stage 1). */
  rollback(tenantId: string, toVersion: number, actor: Actor): Promise<RollbackResult>;
  startExport(tenantId: string, requestedBy: string, kind: ExportKind): Promise<{ exportId: string }>;
  scheduleReviewRequest(requestId: string): Promise<void>;
  cancelReviewRequest(requestId: string): Promise<void>;
  /** Proxies a WebSocket upgrade to the tenant's TenantAgent. */
  tenantSocket(tenantId: string, request: Request): Promise<Response>;
  /** Starts the offboarding Workflow (WP-BILL's `customer.subscription.deleted`, or the daily sweep). Idempotent. */
  startOffboarding(tenantId: string, reason: OffboardReason): Promise<{ workflowId: string }>;
  /** Starts the domain-connect Workflow for one domain connect row: zone, 1:1 copy, diff, then the nameserver hand over. Idempotent. */
  startDomainConnect(tenantId: string, domainConnectId: string): Promise<{ workflowId: string }>;
  /** Answers a blocked or waiting domain-connect run. */
  signalDomainConnect(tenantId: string, domainConnectId: string, signal: DomainSignal): Promise<void>;
  /** Streams a ready export's zip when the link's signature and expiry check out, else 404. The caller checks the owner's session first. */
  exportDownload(tenantId: string, exportId: string, token: string): Promise<Response>;
  /** A client's grid, money services, scans, schedule, day 90 read and today's budget, for the console. */
  scanOverview(tenantId: string): Promise<z.infer<typeof ClientScans>>;
  /** Sets the grid. Refused once the baseline has started: the grid never changes (c011). */
  setScanGrid(tenantId: string, body: z.infer<typeof GridSetBody>, actor: Actor): Promise<z.infer<typeof ScanGridView>>;
  setMoneyServices(tenantId: string, body: z.infer<typeof MoneyServicesSetBody>, actor: Actor): Promise<z.infer<typeof MoneyServicesView>>;
  /** Starts scan Workflows under the daily budget (S13). Idempotent: the same scan never starts twice. */
  startScan(tenantId: string, request: z.infer<typeof ScanStartBody>, actor: Actor): Promise<z.infer<typeof ScanStartResult>>;
  scanPoints(tenantId: string, gridRunId: string): Promise<z.infer<typeof GridPoints>>;
  scanRoster(tenantId: string, gridRunId: string): Promise<z.infer<typeof ScanRoster>>;
  /** A prospect scan as a prospect may see it (D-a applied). The entry point for the reply asset; no page uses it yet. */
  prospectMap(tenantId: string, gridRunId: string): Promise<z.infer<typeof ProspectMap>>;
  scanBudget(): Promise<z.infer<typeof ScanBudgetView>>;
  reportList(tenantId: string): Promise<z.infer<typeof ConsoleReportList>>;
  reportGet(tenantId: string, key: string): Promise<z.infer<typeof ConsoleReport>>;
  /** Edit the words, hold, release, refresh or publish. Publishing tells the client; nothing else does. */
  reportAction(tenantId: string, key: string, body: z.infer<typeof ReportActionBody>, actor: Actor): Promise<z.infer<typeof ConsoleReport>>;
  /**
   * The photo library changed (k11): re-reads "enough photos to go live" (c057), moves the photos step and its
   * clock pause to match, and queues one publish for a burst of changes when the pages could differ.
   */
  mediaChanged(tenantId: string, change: MediaChange): Promise<{ photosStep: z.infer<typeof OnboardingStepState> | null; publishQueued: boolean }>;
  /**
   * Stripe's `invoice.payment_failed` (the app's webhook): starts the failed payment ladder once, or moves it on as
   * far as today allows (c014). Never cancels. Idempotent: a retried event changes nothing.
   */
  paymentFailed(tenantId: string): Promise<{ day: number; did: string[] } | null>;
  /** Stripe's `invoice.paid`: a client in the ladder is billing and live again. A client not in it is untouched. */
  paymentRecovered(tenantId: string): Promise<{ resumed: boolean }>;
  /**
   * The support desk (SUP-D1): makes the request a ticket and starts the support Workflow, which gathers the client's
   * context, sorts the request, drafts the change through the change Workflow where the platform can make it, drafts
   * the reply and flags risk, then waits for a person. Idempotent per request. Without the support Workflow bound it
   * starts the change Workflow, exactly as before.
   */
  startSupport(requestId: string, start: SupportStart): Promise<{ ticketId: string | null; workflowId: string | null }>;
  /** Tells a waiting support Workflow a person approved (send the reply once the change has landed) or closed it. */
  supportSignal(ticketId: string, signal: SupportSignal): Promise<void>;
  /**
   * One reply to email one in: stored once, matched to its business, classified (rules, then the model under a 0.90
   * floor), acted on. Every unsubscribe is suppressed before this returns. A yes starts the recon (docs/12 §9).
   */
  replyIntake(reply: z.infer<typeof ProspectReplyIn>, actor: Actor): Promise<z.infer<typeof ProspectReplyIntakeResult>>;
  /** The staff console's reply queue: what's waiting on Fletcher, or every reply. Says so when the prospects database isn't connected. */
  replyQueue(view: 'waiting' | 'all'): Promise<z.infer<typeof ProspectReplyQueue>>;
  /** Approve and send, mark sent, reclassify, retry or close one reply. */
  replyAction(replyId: string, body: z.infer<typeof ProspectReplyActionBody>, actor: Actor): Promise<z.infer<typeof ProspectReplyQueueItem>>;
}

export type JobsRpcMethod = keyof JobsRpc;
export const JOBS_RPC_METHODS = Object.keys(JOBS_RPC_ARGS) as JobsRpcMethod[];

/** Parse an RPC call's arguments; throws a ZodError on bad input. */
export function parseRpcArgs<K extends JobsRpcMethod>(method: K, args: unknown[]): z.infer<(typeof JOBS_RPC_ARGS)[K]> {
  return JOBS_RPC_ARGS[method].parse(args) as z.infer<(typeof JOBS_RPC_ARGS)[K]>;
}

/** Workflows `apps/jobs` owns (docs/55 WP-JOBS). */
export const WORKFLOWS = ['publish', 'onboarding', 'change-request', 'domain-connect', 'export', 'offboarding', 'retention', 'monthly-report', 'backup', 'scan', 'support'] as const;
export const WorkflowName = z.enum(WORKFLOWS);
